<template>
    <VGoogleMap
            ref="gMap"
            :api-key="apiKey"
            :libraries="libraries"
    >
        <slot/>
    </VGoogleMap>
</template>

<script>
    /**
     * Every map on the site, and the one place the Google Maps api is configured.
     *
     * vue2-google-maps was a global plugin: `Vue.use(VueGoogleMaps, {load: {key, libraries}})`
     * in index.js, which put the maps `<script>` on every page whether or not it drew a map.
     * vue3-google-map has no install step - a map loads the api when it mounts, and the two
     * pages that show one are the only ones that pay for it.
     *
     * The catch is that the configuration is still per page, not per map: `setOptions` inside
     * the library runs once, for whichever map mounts first, and a second map asking for a
     * different key or a different library list is ignored with a console warning. So the key
     * and the libraries are spelled here rather than at the call sites, and every map on a
     * page asks for the same ones.
     *
     * Everything else - `center`, `zoom`, `style` and the map options, which are individual
     * props on vue3-google-map rather than one `options` object - falls through from the call
     * site: this component declares no props of its own, so its attrs land on VGoogleMap and
     * resolve against its props there.
     */
    import { GoogleMap as VGoogleMap } from 'vue3-google-map';

    // `maps` is the map itself, `marker` carries google.maps.Marker (the <Marker> component
    // and placeFinder's draggable one alike), `places` is the autocomplete and PlacesService.
    // The dynamic loader fetches exactly what is listed, so an omission is a missing
    // constructor at the call site rather than a slower load
    const LIBRARIES = ['maps', 'marker', 'places'];

    export default {
        name: 'GoogleMap',
        components: { VGoogleMap },
        data () {
            return {
                apiKey: GOOGLE_API_KEY,
                libraries: LIBRARIES,
            };
        },
        created () {
            /**
             * Settles with the map and the api once Google has handed them over, which is
             * what vue2-google-maps' `$mapPromise` did.
             *
             * A parent that wants to build on the map - a PlacesService, a marker it moves
             * itself - cannot do it in its own `mounted`: the map is a network round trip
             * away at that point, and `$refs.gMap.map` is still undefined.
             */
            this.mapPromise = new Promise(resolve => {
                this.resolveMapPromise = resolve;
            });
        },
        mounted () {
            const gMap = this.$refs.gMap;
            // `ready` is a ref returned from VGoogleMap's setup, so reading it through the
            // instance is a reactive read and this watcher hears the api arriving
            this.$watch(() => gMap.ready, ready => {
                if (ready) {
                    this.resolveMapPromise({ map: gMap.map, api: gMap.api });
                }
            }, { immediate: true });
        },
    };
</script>
